import {
  Sparkles,
  HandHeart,
  Flower2,
  HeartPulse,
  Sun,
  Zap,
  CalendarCheck,
  Phone,
} from "lucide-react";
import { SITE } from "../lib/site";
import { Link } from "react-router-dom";

// 6 macro-categorie di servizi del centro. L'ordine rispetta la specifica
// fornita dalla titolare.
const CATEGORIES = [
  {
    icon: Sparkles,
    title: "Viso",
    subtitle: "Trattamenti per la cura e bellezza del viso",
    services: [
      { name: "Pulizia viso avanzata" },
      { name: "Trattamenti specifici viso" },
      { name: "Laminazione ciglia", exclusive: true },
    ],
  },
  {
    icon: HeartPulse,
    title: "Massaggi",
    subtitle: "Momenti di benessere per corpo e mente",
    services: [
      { name: "Massaggio relax total body" },
      { name: "Massaggio decontratturante schiena" },
      { name: "Linfodrenaggio" },
      { name: "Hot Stone" },
    ],
  },
  {
    icon: Flower2,
    title: "Trattamenti corpo",
    subtitle: "Rimodellare, tonificare, rinnovare",
    services: [
      { name: "Expert Forms", exclusive: true },
      { name: "Pressomassaggio" },
      { name: "Bendaggi corpo" },
      { name: "Scrub corpo" },
    ],
  },
  {
    icon: HandHeart,
    title: "Mani & Piedi",
    subtitle: "Cura completa di mani e piedi",
    services: [
      { name: "Manicure con semipermanente" },
      { name: "Ricostruzione unghie" },
      { name: "Pedicure con callus peeling" },
      { name: "Pedicure con semipermanente" },
    ],
  },
  {
    icon: Zap,
    title: "Epilazione",
    subtitle: "Epilazione professionale e duratura",
    services: [
      { name: "Epilazione laser", exclusive: true },
      { name: "Epilazione corpo donna" },
      { name: "Epilazione corpo uomo" },
    ],
  },
  {
    icon: Sun,
    title: "Solarium",
    subtitle: "Abbronzatura sicura tutto l'anno",
    services: [
      { name: "Lettini abbronzanti" },
      { name: "Docce solari" },
      { name: "Trifacciale" },
    ],
  },
];

export default function Services() {
  return (
    <section
      id="servizi"
      data-testid="services-section"
      translate="no"
      lang="it"
      className="notranslate relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <div className="ee-eyebrow">Trattamenti</div>
          <h2
            data-testid="services-title"
            className="font-serif-display mt-4 text-[34px] sm:text-[46px] lg:text-[54px] leading-[1.08] tracking-[-0.01em] text-[var(--ee-ink)]"
          >
            I nostri <em className="italic text-[var(--ee-rose-strong)]">trattamenti</em>.
          </h2>
          <p className="mt-5 text-[15px] sm:text-[16px] leading-[1.7] text-[var(--ee-ink-soft)]">
            Una selezione curata di trattamenti per viso, corpo, mani, piedi
            e benessere — con uno standard costante di cura e qualità.
          </p>
        </div>

        {/* 6 macro-categorie */}
        <div
          data-testid="services-grid"
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 auto-rows-fr"
        >
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`service-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="ee-card p-5 sm:p-6 flex flex-col gap-3"
                style={{ animation: `ee-fade-up 0.7s ease-out ${i * 0.06}s both` }}
              >
                <div className="flex items-start gap-2.5">
                  <span className="w-9 h-9 rounded-full bg-[var(--ee-blush-soft)] flex items-center justify-center text-[var(--ee-rose-strong)] flex-shrink-0">
                    <Icon size={17} strokeWidth={1.6} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-serif-display text-[20px] leading-tight text-[var(--ee-ink)]">
                      {c.title}
                    </h3>
                    <p className="mt-0.5 text-[12px] text-[var(--ee-ink-soft)] leading-snug">
                      {c.subtitle}
                    </p>
                  </div>
                </div>

                <ul className="mt-1 space-y-1.5 text-[13px] text-[var(--ee-ink)] leading-snug">
                  {c.services.map((s) => (
                    <li
                      key={s.name}
                      className="flex items-start gap-2.5"
                    >
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-[var(--ee-rose-strong)] flex-shrink-0" />
                      <span className="flex-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span>{s.name}</span>
                        {s.exclusive && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[var(--ee-blush-soft)] text-[9.5px] uppercase tracking-[0.14em] text-[var(--ee-rose-strong)] font-medium">
                            Esclusivo
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Banner di chiusura */}
        <div
          data-testid="services-banner"
          className="mt-14 ee-card bg-[var(--ee-blush-soft)] border-[var(--ee-blush)] p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
        >
          <div className="max-w-xl">
            <h3 className="font-serif-display text-[22px] sm:text-[26px] leading-tight text-[var(--ee-ink)]">
              E molti altri trattamenti specifici.
            </h3>
            <p className="mt-2 text-[13.5px] sm:text-[14px] text-[var(--ee-ink-soft)] leading-snug">
              Chiamaci per scoprire il percorso più adatto a te.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={SITE.phoneTel}
              data-testid="services-call-btn"
              className="ee-btn ee-btn-primary"
            >
              <Phone size={16} strokeWidth={1.8} />
              Chiama ora
            </a>
            <Link
              to="/prenota"
              data-testid="services-book-btn"
              className="ee-btn ee-btn-outline"
            >
              <CalendarCheck size={16} strokeWidth={1.8} />
              Prenota online
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
